import { useCallback, useRef, useState } from "react";
import { FaPhoneAlt, FaPaperPlane, FaUpload } from "react-icons/fa";
import CircleIconAnimationButton from "../../components/ui/circle-icon-animation-button";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SectionSepratorHeader from "../../components/ui/section-seprator-header";
import FitWidthText from "./hero-text";
import Noise from "../../components/core/noise";

gsap.registerPlugin(ScrollTrigger);

const ContactSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap.to(".contact-watermark", {
        y: -50,
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });

      // Animated Bridge Elements
      gsap.fromTo(
        ".bridge-text-bg",
        { y: 100 },
        {
          y: -100,
          scrollTrigger: {
            trigger: ".bridge-container",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );

      gsap.fromTo(
        ".bridge-text-fg",
        { y: 50 },
        {
          y: -50,
          scrollTrigger: {
            trigger: ".bridge-container",
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );
    },
    { scope: containerRef },
  );

  const contactNumbers = [
    { label: "TOLL FREE", number: "+1.855.638.2539", href: "tel:18556382539" },
    {
      label: "NORTHERN AB",
      number: "+1.780.975.2558",
      href: "tel:17809752558",
    },
    {
      label: "SOUTHERN AB",
      number: "+1.403.437.2600",
      href: "tel:14034372600",
    },
  ];
  const [fontSize, setFontSize] = useState<string | number>("auto");

  const getFontSize = useCallback((size: string | number) => {
    setFontSize(size);
  }, []);
  return (
    <>
      <section
        ref={containerRef}
        className="w-full mt-14 z-2 bg-[#111212] relative overflow-hidden"
      >
        <Noise className="opacity-[0.02] pointer-events-none" />
        <SectionSepratorHeader
          title="DIRECT ENGAGEMENT UNIT"
          subtitle={
            <>
              Initialize{" "}
              <span className="text-[#4991b8] italic">Structural&nbsp;</span>
              Inquiry
            </>
          }
          description="Connect with our industrial procurement specialists to begin the blueprint certification process."
          className="mb-20"
        />
        <div className=" mx-auto px-4 md:px-6 relative z-10 w-full">
          <div className="w-full bg-[#f0f0f0] border border-white/20 rounded-[2.5rem] shadow-3xl overflow-hidden relative">
            <Noise className="opacity-[0.08] pointer-events-none" />
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-2/5 p-10 md:p-14 border-b lg:border-b-0 lg:border-r border-black/5 bg-black/3 relative overflow-hidden group">
                <div className="relative z-10">
                  <h3 className="text-3xl font-black text-[#111212] uppercase tracking-tighter mb-4">
                    Lead Protocols
                  </h3>
                  <p className="text-[#111212]/60 font-medium mb-12 text-sm leading-relaxed">
                    Our team provides comprehensive engineering support for
                    developers, marketers, and business owners. Select a
                    regional hub to initiate focus.
                  </p>

                  <div className="flex flex-col gap-6">
                    {contactNumbers.map((item, idx) => (
                      <a
                        key={idx}
                        href={item.href}
                        className="flex items-center gap-5 group/item p-4 rounded-2xl bg-white/50 border border-black/5 hover:border-[#4991b8]/30 transition-all duration-500"
                      >
                        <div className="w-10 h-10 rounded-xl bg-[#4991b8]/10 border border-[#4991b8]/20 flex items-center justify-center text-[#4991b8] group-hover/item:bg-[#4991b8] group-hover/item:text-black transition-all">
                          <FaPhoneAlt size={14} />
                        </div>
                        <div>
                          <p className="text-[9px] font-black tracking-[0.3em] text-[#111212]/30 uppercase mb-1">
                            {item.label}
                          </p>
                          <p className="text-lg font-black text-[#111212] group-hover/item:text-[#4991b8] transition-colors uppercase">
                            {item.number}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
                <div className="contact-watermark absolute -bottom-10 left-0 text-[18rem] font-black text-black/15 select-none pointer-events-none transition-colors duration-1000 group-hover:text-[#4991b8]/15">
                  FX
                </div>
              </div>
              <div className="lg:w-3/5 p-10 md:p-14 bg-white/40 backdrop-blur-sm">
                <form className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  {[
                    {
                      label: "Name",
                      placeholder: "Contact Personnel",
                      type: "text",
                    },
                    {
                      label: "Company",
                      placeholder: "Industrial Entity",
                      type: "text",
                    },
                    {
                      label: "Email",
                      placeholder: "office@brand.com",
                      type: "email",
                    },
                    {
                      label: "Phone",
                      placeholder: "+1 (---) --- ----",
                      type: "tel",
                    },
                  ].map((field, i) => (
                    <div key={i} className="flex flex-col gap-2">
                      <label className="text-[10px] font-black text-[#111212]/40 uppercase tracking-widest px-1">
                        {field.label} <span className="text-[#4991b8]">*</span>
                      </label>
                      <input
                        type={field.type}
                        className="w-full bg-white border border-black/5 rounded-xl px-5 py-4 text-[#111212] focus:outline-none focus:border-[#4991b8]/50 transition-all placeholder:text-[#111212]/20 font-medium"
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}
                  <div className="md:col-span-2 flex flex-col gap-2 mt-2">
                    <label className="text-[10px] font-black text-[#111212]/40 uppercase tracking-widest px-1">
                      Blueprint / Design Specs
                    </label>
                    <div className="w-full border border-dashed border-black/10 rounded-2xl p-8 flex flex-col items-center justify-center gap-3 bg-white/30 hover:bg-white/60 hover:border-[#4991b8]/20 transition-all cursor-pointer group/upload">
                      <FaUpload className="text-[#111212]/30 group-hover/upload:text-[#4991b8] transition-colors" />
                      <p className="text-[#111212]/50 text-xs font-bold uppercase tracking-widest">
                        <span className="text-[#111212]">Upload</span> or drag
                        files here
                      </p>
                    </div>
                  </div>
                  <div className="md:col-span-2 flex flex-col gap-2">
                    <label className="text-[10px] font-black text-[#111212]/40 uppercase tracking-widest px-1">
                      Project Specification
                    </label>
                    <textarea
                      rows={3}
                      className="w-full bg-white border border-black/5 rounded-xl px-5 py-4 text-[#111212] focus:outline-none focus:border-[#4991b8]/50 transition-all placeholder:text-[#111212]/20 resize-none font-medium"
                      placeholder="Detail the structural requirements..."
                    />
                  </div>

                  <div className="md:col-span-2 mt-6 max-w-fit">
                    <CircleIconAnimationButton
                      type="submit"
                      icon={<FaPaperPlane size={14} />}
                      className="w-full [&>button]:w-full"
                    >
                      Transmit Inquiry
                    </CircleIconAnimationButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="bridge-container w-full relative h-[60vh] translate-y-15 flex flex-col items-center justify-center bg-[#111212]">
        <div className="absolute inset-0 pointer-events-none opacity-20">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-full bg-linear-to-b from-[#4991b8]/0 via-[#4991b8]/30 to-[#4991b8]/0" />
          <div className="absolute top-1/4 left-0 w-full h-px bg-white/5" />
          <div className="absolute top-2/4 left-0 w-full h-px bg-white/5" />
          <div className="absolute top-3/4 left-0 w-full h-px bg-white/5" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-4 text-center">
          <span className="text-[#4991b8] font-black tracking-[0.8em] uppercase text-[10px] block mb-8 opacity-40">
            Technical Specification
          </span>
          <div className="relative w-full flex flex-col items-center justify-center">
            <div className="bridge-text-bg absolute inset-0 flex flex-col items-center justify-center opacity-10 blur-[1px]">
              <span
                style={{ fontSize: `${Number(fontSize) * 1.2}px` }}
                className="font-black text-transparent stroke-1 stroke-white uppercase leading-[0.8]"
              >
                INDUSTRIAL
              </span>
              <span
                style={{ fontSize: `${Number(fontSize) * 1.2}px` }}
                className="font-black text-transparent stroke-1 stroke-white uppercase leading-[0.8]"
              >
                EXPERTISE
              </span>
            </div>
            <FitWidthText
              getfontSize={getFontSize}
              className="bridge-text-fg relative z-20"
            >
              <span className="text-white font-black drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]">
                INDUSTRIAL
              </span>
              <span className="text-[#4991b8] italic font-black">
                EXPERTISE
              </span>
            </FitWidthText>
          </div>
          <div className="mt-12 flex items-center gap-10 opacity-30">
            <div className="w-20 h-px bg-linear-to-r from-transparent to-[#4991b8]/40" />
            <div className="relative w-12 h-12 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border border-dashed border-[#4991b8]/30 animate-[spin_12s_linear_infinite]" />
              <div className="absolute inset-2 rounded-full border border-[#4991b8]/20 animate-[spin_7s_linear_infinite_reverse]" />
              <div className="absolute w-full h-px bg-[#4991b8]/20" />
              <div className="absolute h-full w-px bg-[#4991b8]/20" />
              <div className="w-2 h-2 rounded-full bg-[#4991b8]/60 shadow-[0_0_8px_rgba(73,145,184,0.6)] animate-pulse" />
            </div>
            <div className="w-20 h-px bg-linear-to-l from-transparent to-[#4991b8]/40" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-64 bg-linear-to-t from-[#111212] to-transparent z-30" />
      </div>
    </>
  );
};

export default ContactSection;
